import { useState, useEffect, useCallback, useRef } from 'react';
import { registerSW } from 'virtual:pwa-register';

const UPDATE_CHECK_INTERVAL_MS = 60_000;
const REMIND_UPDATE_AFTER_MS = 10 * 60_000;
const disableAutoUpdate =
  import.meta.env.VITE_DISABLE_WORKER_AUTO_UPDATE === 'TRUE';
const disableNotification =
  import.meta.env.VITE_DISABLE_WORKER_NOTIFICATION === 'TRUE';

export const useServiceWorker = () => {
  const [showWorkerNotification, setShowWorkerNotification] =
    useState<boolean>(false);
  const updateServiceWorker = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    let updateCheckInterval: number | undefined;

    updateServiceWorker.current = registerSW({
      immediate: true,
      onNeedRefresh() {
        if (disableNotification) {
          void updateServiceWorker.current?.();
          return;
        }
        setShowWorkerNotification(true);
      },
      onRegisteredSW(_serviceWorkerUrl, registration) {
        if (!registration || disableAutoUpdate) return;

        // Check periodically so an already-open tab notices a newly deployed build.
        updateCheckInterval = window.setInterval(() => {
          void registration.update().catch(() => undefined);
        }, UPDATE_CHECK_INTERVAL_MS);
      },
    });

    return () => {
      if (updateCheckInterval !== undefined) {
        window.clearInterval(updateCheckInterval);
      }
      updateServiceWorker.current = null;
    };
  }, []);

  const postponeFinishedNotification = useCallback(() => {
    setShowWorkerNotification(false);
    // If postponed, show the update reminder again after ten minutes.
    setTimeout(() => {
      setShowWorkerNotification(true);
    }, REMIND_UPDATE_AFTER_MS);
  }, []);

  const applyUpdate = useCallback(() => {
    void updateServiceWorker.current?.();
  }, []);

  return {
    applyUpdate,
    postponeFinishedNotification,
    showWorkerNotification,
  };
};
